import { useCallback, useId, useState, type FormEvent } from 'react';
import { useProfilePhotoFormState } from '../../features/user/useProfilePhotoFormState';
import {
  useDeleteLacUserPhotoMutation,
  useSetLacUserRolesMutation,
  useUpdateLacUserMutation,
  useUploadLacUserPhotoMutation,
} from '../../features/lac/mutations/useLacUserMutations';
import { useLacUserRolesQuery } from '../../features/lac/queries/useLacUserRolesQuery';
import { downloadLacUserPhoto } from '../../features/lac/downloadLacUserPhoto';
import { useI18n } from '../../i18n/useI18n';
import type { LacUser } from '../../types/lac';
import { isValidEmailFormat } from '../../utils/formUtils';
import { unixMillisToDateInput } from '../../utils/dateTimeUtils';
import { resolveUserRef } from '../../features/user/userDisplay';
import {
  getLacEditRoleOptions,
  isLacClientRole,
  resolveLacClientRoleFromNames,
  type LacClientRole,
} from './lacRoles';
import {
  isLacUserProfileDirty,
  isLacUserProfileSaveDisabled,
  isLacUserValidToBeforeValidFrom,
} from './lacUserProfileFormHelpers';
import { submitLacUserProfileForm } from './submitLacUserProfileForm';

export type UseLacUserProfileFormArgs = {
  profile: LacUser;
  tenantName: string;
  userRef: string;
};

export function useLacUserProfileForm({
  profile,
  tenantName,
  userRef,
}: UseLacUserProfileFormArgs) {
  const { translate } = useI18n();
  const updateUserMutation = useUpdateLacUserMutation();
  const uploadPhotoMutation = useUploadLacUserPhotoMutation();
  const deletePhotoMutation = useDeleteLacUserPhotoMutation();
  const setUserRolesMutation = useSetLacUserRolesMutation();
  const rolesQuery = useLacUserRolesQuery(tenantName, userRef);
  const loadedRole = resolveLacClientRoleFromNames(rolesQuery.data);

  const photoFieldId = useId();
  const roleFieldId = useId();
  const [photoReloadKey, setPhotoReloadKey] = useState(0);

  const photoUserRef = resolveUserRef(profile);

  const loadPhoto = useCallback(
    () => downloadLacUserPhoto(tenantName, photoUserRef),
    [tenantName, photoUserRef],
  );

  const {
    previewSrc: photoDisplaySrc,
    photoError,
    photoChanged,
    pendingFile,
    removePhoto,
    handlePhotoChange,
    handleRemovePhoto,
    resetPhotoEdits,
  } = useProfilePhotoFormState({
    loadPhoto,
    translate,
    invalidTypeKey: 'lac.users.profile.photoInvalidType',
    tooLargeKey: 'lac.users.profile.photoTooLarge',
    reloadKey: photoReloadKey,
  });

  const username = profile.name ?? '';
  const [email, setEmail] = useState(profile.email ?? '');
  const [firstName, setFirstName] = useState(profile.firstName ?? '');
  const [lastName, setLastName] = useState(profile.lastName ?? '');
  const [roleOverride, setRoleOverride] = useState<LacClientRole | null>(null);
  const [validFrom, setValidFrom] = useState(
    unixMillisToDateInput(profile.validFrom),
  );
  const [validTo, setValidTo] = useState(
    unixMillisToDateInput(profile.validTo),
  );

  const role = roleOverride ?? loadedRole;
  const isPhotoBusy =
    uploadPhotoMutation.isPending || deletePhotoMutation.isPending;
  const isBusy =
    updateUserMutation.isPending ||
    setUserRolesMutation.isPending ||
    isPhotoBusy;
  const isSubmitting =
    updateUserMutation.isPending || setUserRolesMutation.isPending;
  const isRoleLoading = rolesQuery.isLoading;
  const isRoleLoadError = rolesQuery.isError;

  const roleOptions = getLacEditRoleOptions(loadedRole).map((value) => ({
    value,
    label: translate(`lac.users.roles.${value}`),
  }));

  const trimmedEmail = email.trim();
  const isEmailInvalid =
    trimmedEmail !== '' && !isValidEmailFormat(trimmedEmail);
  const trimmedUsername = username.trim();
  const isValidToBeforeValidFrom = isLacUserValidToBeforeValidFrom(
    validFrom,
    validTo,
  );

  const isDirty = isLacUserProfileDirty({
    profile,
    email,
    firstName,
    lastName,
    validFrom,
    validTo,
    roleOverride,
    loadedRole,
    photoChanged,
    photoError,
  });

  const isSaveDisabled = isLacUserProfileSaveDisabled({
    isDirty,
    isBusy,
    isRoleLoading,
    isRoleLoadError,
    email,
    username,
    firstName,
    lastName,
    validFrom,
    validTo,
    photoError,
  });

  const handleCancel = () => {
    setEmail(profile.email ?? '');
    setFirstName(profile.firstName ?? '');
    setLastName(profile.lastName ?? '');
    setRoleOverride(null);
    setValidFrom(unixMillisToDateInput(profile.validFrom));
    setValidTo(unixMillisToDateInput(profile.validTo));
    resetPhotoEdits();
  };

  const handleRoleChange = (value: string) => {
    if (isLacClientRole(value)) {
      setRoleOverride(value);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaveDisabled) {
      return;
    }

    submitLacUserProfileForm({
      translate,
      tenantName,
      userRef,
      profile,
      trimmedUsername,
      trimmedEmail,
      firstName,
      lastName,
      validFrom,
      validTo,
      role,
      loadedRole,
      isRoleLoadError,
      rolesData: rolesQuery.data,
      photoChanged,
      pendingFile,
      removePhoto,
      updateUserMutation,
      setUserRolesMutation,
      uploadPhotoMutation,
      deletePhotoMutation,
      bumpPhotoReloadKey: () => {
        setPhotoReloadKey((key) => key + 1);
      },
    });
  };

  return {
    translate,
    photoFieldId,
    roleFieldId,
    photoDisplaySrc,
    photoError,
    handlePhotoChange,
    handleRemovePhoto,
    username,
    email,
    setEmail,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    role,
    validFrom,
    setValidFrom,
    validTo,
    setValidTo,
    isPhotoBusy,
    isBusy,
    isSubmitting,
    isRoleLoading,
    isRoleLoadError,
    roleOptions,
    isEmailInvalid,
    isValidToBeforeValidFrom,
    isDirty,
    isSaveDisabled,
    handleCancel,
    handleRoleChange,
    handleSubmit,
  };
}
